import { useState, useEffect } from 'react'

/**
 * Captures the browser's beforeinstallprompt event so we can trigger it later.
 * Returns { deferredPrompt, promptInstall, isInstalled, isSupported }.
 *
 * isInstalled  – true when running in standalone/PWA mode (already on home screen)
 * isSupported  – true when the browser fired beforeinstallprompt (Chrome/Edge/Android)
 * promptInstall – async function that shows the native install prompt
 *
 * The event is captured early in index.js (before React mounts) and stored on
 * window.__pwaInstallPrompt so it is never missed on Android Chrome.
 */
export default function useInstallPrompt() {
  // Seed state from the early-captured global so we don't miss the event.
  const [deferredPrompt, setDeferredPrompt] = useState(
    () => window.__pwaInstallPrompt ?? null
  )

  const isInstalled =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true

  useEffect(() => {
    // Also pick up the prompt if it fires after React mounts.
    const handler = () => {
      if (window.__pwaInstallPrompt) {
        setDeferredPrompt(window.__pwaInstallPrompt)
      }
    }
    window.addEventListener('pwaInstallPromptReady', handler)
    return () => window.removeEventListener('pwaInstallPromptReady', handler)
  }, [])

  const promptInstall = async () => {
    if (!deferredPrompt) return false
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    setDeferredPrompt(null)
    return outcome === 'accepted'
  }

  return {
    deferredPrompt,
    isInstalled,
    isSupported: Boolean(deferredPrompt),
    promptInstall,
  }
}
